import React from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';

/** 「アクション」メニューの項目 */
export type ConsoleHeaderAction = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  /** 破壊的操作（赤字表示 + 区切り上に配置） */
  danger?: boolean;
  disabled?: boolean;
};

type Props = {
  title: string;
  /** リソース件数（AWSコンソール風の "(N)" 表示） */
  count?: number;
  /** 見出し下の補足 */
  description?: string;
  /** 右側のアクション（作成ボタン等） */
  actions?: React.ReactNode;
  /** 「アクション」ドロップダウンに入れる副次操作（一括登録・テンプレDL・削除など） */
  menuActions?: ConsoleHeaderAction[];
  /** 再読み込みハンドラ（指定時に更新アイコンを表示） */
  onRefresh?: () => void;
  /** 戻る導線（研修管理トップなどへ）。指定時にパンくず風の戻るリンクを表示 */
  backLabel?: string;
  onBack?: () => void;
};

/**
 * AWSマネジメントコンソール風のリソースヘッダーバー。
 * 「タイトル (件数)」＋更新アイコン＋「アクション」メニュー＋右寄せ主アクション。
 * 既存MUIトーンで表現する。
 */
const ConsoleHeader: React.FC<Props> = ({
  title,
  count,
  description,
  actions,
  menuActions,
  onRefresh,
  backLabel,
  onBack,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const hasMenu = !!menuActions && menuActions.length > 0;
  const normalActions = menuActions?.filter((a) => !a.danger) ?? [];
  const dangerActions = menuActions?.filter((a) => a.danger) ?? [];

  return (
    <Box sx={{ mb: 1.5 }}>
      {onBack && (
        <DomainBreadcrumbs
          mb={0.75}
          items={[{ label: backLabel ?? 'ドメイン', onClick: onBack }, { label: title }]}
        />
      )}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" fontWeight={700}>
          {title}
          {count !== undefined && (
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.75 }}>
              ({count})
            </Box>
          )}
        </Typography>
        {onRefresh && (
          <Tooltip title="更新">
            <IconButton size="small" onClick={onRefresh} sx={{ color: 'text.secondary' }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {hasMenu && (
          <>
            <Button
              variant="outlined"
              size="small"
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              アクション
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    mt: 0.5,
                    minWidth: 200,
                  },
                },
              }}
            >
              {normalActions.map((a) => (
                <MenuItem
                  key={a.label}
                  disabled={a.disabled}
                  onClick={() => {
                    setAnchorEl(null);
                    a.onClick();
                  }}
                  sx={{ fontSize: 14 }}
                >
                  {a.icon && <ListItemIcon sx={{ color: 'text.secondary' }}>{a.icon}</ListItemIcon>}
                  {a.label}
                </MenuItem>
              ))}
              {dangerActions.length > 0 && normalActions.length > 0 && <Divider />}
              {dangerActions.map((a) => (
                <MenuItem
                  key={a.label}
                  disabled={a.disabled}
                  onClick={() => {
                    setAnchorEl(null);
                    a.onClick();
                  }}
                  sx={{ fontSize: 14, color: 'error.main' }}
                >
                  {a.icon && <ListItemIcon sx={{ color: 'error.main' }}>{a.icon}</ListItemIcon>}
                  {a.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {actions}
      </Stack>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default ConsoleHeader;
